import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CalendarPlus, Share2, Check, Users, Clock, CalendarDays, Tag } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Seo from "@/components/Seo";
import { Overline } from "@/components/Cards";
import { toast } from "sonner";

function googleCalUrl(p) {
  if (!p.next_date) return null;
  const s = (p.start_time || "17:00").replace(":", "");
  const e = (p.end_time || "22:00").replace(":", "");
  const d = p.next_date.replace(/-/g, "");
  const dates = `${d}T${s}00/${d}T${e}00`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `${p.title} — Visit RiNo`,
    details: p.description || "",
    location: "River North Art District, Denver, CO",
    dates,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function PromotionDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [going, setGoing] = useState(false);

  useEffect(() => {
    api.get(`/promotions/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
    setGoing(!!localStorage.getItem(`going_${slug}`));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-400">Promotion not found. <Link to="/promotions" className="text-[#FF0055]">Back to promotions</Link></div>;
  if (!p) return <div className="py-32 text-center text-neutral-500">Loading...</div>;

  const rsvp = async () => {
    try {
      const { data } = await api.post(`/promotions/${slug}/rsvp`, {});
      setP({ ...p, going_count: data.going_count });
      setGoing(true);
      localStorage.setItem(`going_${slug}`, "1");
      toast.success(data.message);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const share = async () => {
    const url = window.location.href;
    const payload = { title: `${p.title} — Visit RiNo`, text: p.subtitle || p.description, url };
    if (navigator.share) { try { await navigator.share(payload); } catch {} }
    else { navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
  };

  const gcal = googleCalUrl(p);

  return (
    <div>
      <Seo title={p.title} description={p.subtitle || p.description?.slice(0, 155)} image={p.image} type="article" />
      <div className="relative h-[42vh] overflow-hidden grain">
        <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-8">
            <Link to="/promotions" className="inline-flex items-center gap-2 text-neutral-300 hover:text-white text-sm mb-4" data-testid="back-to-promotions"><ArrowLeft size={16} /> Promotions</Link>
            <Overline>{p.day}</Overline>
            <h1 className="font-display font-black text-4xl sm:text-6xl text-white mt-2">{p.title}</h1>
            <p className="text-[#FFEA00] text-lg mt-1">{p.subtitle}</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <p className="text-neutral-300 text-lg leading-relaxed">{p.description}</p>

          <div className="flex flex-wrap gap-4 mt-6 text-sm text-neutral-400">
            {p.next_date && <span className="flex items-center gap-2"><CalendarDays size={16} className="text-[#FF0055]" /> Next: {new Date(p.next_date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>}
            {(p.start_time || p.end_time) && <span className="flex items-center gap-2"><Clock size={16} className="text-[#FF0055]" /> {p.start_time} – {p.end_time}</span>}
          </div>

          {/* Participating businesses + their offers */}
          <div className="mt-10">
            <h2 className="font-display font-bold text-2xl text-white mb-4">Who's Participating</h2>
            <div className="space-y-3" data-testid="participating-list">
              {p.participating?.map((biz, i) => (
                <div key={i} className="flex items-start gap-4 border border-white/10 bg-[#141414] p-4">
                  <Tag size={18} className="text-[#00FF66] mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    {biz.slug ? (
                      <Link to={`/directory/${biz.slug}`} className="text-white font-semibold hover:text-[#FF0055]" data-testid={`promo-biz-${biz.slug}`}>{biz.name}</Link>
                    ) : <span className="text-white font-semibold">{biz.name}</span>}
                    {biz.offer && <div className="text-[#FFEA00] text-sm mt-0.5">{biz.offer}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Interactive sidebar */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-32 border border-white/10 bg-[#141414] p-6 space-y-4">
            <div className="flex items-center gap-2 text-white">
              <Users size={18} className="text-[#00E5FF]" />
              <span className="font-display font-extrabold text-2xl" data-testid="going-count">{p.going_count}</span>
              <span className="text-neutral-400 text-sm">going</span>
            </div>
            <button onClick={rsvp} disabled={going} data-testid="rsvp-btn"
              className={`w-full py-3 font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${going ? "bg-[#00FF66] text-black" : "bg-[#FF0055] hover:bg-[#ff2d74] text-white"}`}>
              {going ? <><Check size={16} /> You're Going!</> : "I'm Going"}
            </button>
            {gcal && (
              <a href={gcal} target="_blank" rel="noopener noreferrer" data-testid="add-calendar-btn"
                className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
                <CalendarPlus size={16} /> Add to Calendar
              </a>
            )}
            <button onClick={share} data-testid="share-btn"
              className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
              <Share2 size={16} /> Share
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
